/**
 * @module  public/js/utils/cacheUtils.js
 * @summary Tiny memoisation helper for async fetches.
 *
 * @description
 * Provides a single `getCached(cache, key, loader)` function that:
 *  • Returns `cache[key]` immediately if present.
 *  • Otherwise awaits `loader(key)` and stores the resolved value.
 *
 * Designed for per-page in-memory caches (AI reports, contact meta, etc.).
 *
 * @version 1.0.0
 * @since   2025-05-02
 */

/* eslint-disable import/prefer-default-export */

/**
 * @template T
 * @param {Record<string, T>} cache – mutable in-memory object store
 * @param {string} key              – cache key
 * @param {(k: string) => Promise<T>} loader – async fetch fn
 * @returns {Promise<T>} resolved value
 */
export async function getCached(cache, key, loader) {
  if (Object.prototype.hasOwnProperty.call(cache, key)) return cache[key];
  // eslint-disable-next-line no-param-reassign
  cache[key] = await loader(key);
  return cache[key];
}
